'use client';

// base
import React, { useEffect, useRef, useState } from 'react';

// styles
import { Column, Row } from 'styles';

// components
import { BasicLabel, BasicSelect, BasicTextField, SingleDatepicker, TextButton } from 'components';
import { useFetchNations } from 'domains/Setting';
import {
  IUserDetail,
  ReqUpdateUser,
  useDeleteUser,
  useFetchUserDetail,
  useUpdateUser,
  useUpdateUserPhoto
} from 'domains/Admin';
import { useAlert } from 'contexts';
import { dateToString, stringToDate } from 'utils';
import { cookieStorage } from 'services';
import { useRouter } from 'next/navigation';
import { ChangePassword } from 'domains/Auth';
import { ChangePasswordForce } from 'domains/Auth/containers/ChagePasswordForce';

interface Props {
  userId: string;
  isMyPage: boolean;
  onClose: () => void;
}

export const UserDetail: React.FC<Props> = ({ userId, onClose, isMyPage }) => {
  const [userDetail, setUserDetail] = useState<IUserDetail>({
    userId: '',
    username: '',
    mobile: '',
    nationId: '',
    mobileNationId: '',
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    bloodType: '',
    iqamaNo: '',
    iqamaExpiryDate: '',
    userFileId: '',
    userFileUrl: '',
    createdDate: '',
    lastModifiedDate: '',
    deleteStatus: false,
    authority: '',
    userStatus: ''
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { data: userDetailDto } = useFetchUserDetail({ data: { userId } });
  const { data: nations } = useFetchNations({ data: {} });
  const { mutateAsync: deleteUser } = useDeleteUser();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { mutateAsync: updateUserPhoto } = useUpdateUserPhoto();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const { showAlert, showConfirmAlert } = useAlert();
  const router = useRouter();

  useEffect(() => {
    if (!userDetailDto) {
      return;
    }
    setUserDetail(userDetailDto);
  }, [userDetailDto]);

  if (!userDetailDto || !nations) {
    return <div></div>;
  }

  return (
    <Column>
      <Row>
        <Column fullWidth>
          <Column gap="9px">
            <BasicLabel label={'Picture'} />
            <div
              style={{
                height: '350px',
                borderRadius: '6px',
                border: '1px solid #DADADA',
                background: '#DADADA',
                cursor: 'pointer'
              }}
              onClick={() => {
                inputRef.current?.click();
              }}
            >
              <input
                type="file"
                accept="image/*"
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) {
                    return;
                  }

                  updateUserPhoto(
                    { data: { userId, file } },
                    {
                      onSuccess: (data) => {
                        showAlert(data.message, () => {});
                      },
                      onError: (error) => {
                        showAlert('Failed to update user photo');
                      }
                    }
                  );
                }}
              />
              <img
                src={userDetail.userFileUrl}
                alt=""
                style={{ width: '100%', height: '100%', borderRadius: '6px' }}
              />
            </div>
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Company'} required />
            <BasicTextField disabled fullWidth value={'Samsung E&A'} />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'ID (Mobile)'} required />
            <Row>
              <BasicSelect
                disabled
                fullWidth
                value={userDetail.mobileNationId}
                options={nations.map((nation) => ({
                  label: (
                    <Row style={{ alignItems: 'center' }}>
                      <img src={nation.flagUri} />
                      <Row gap="0px">
                        <div>{nation.countryCode}</div>
                        <div>{nation.numberCode}</div>
                      </Row>
                    </Row>
                  ),
                  value: nation.nationId
                }))}
              />
              <BasicTextField disabled fullWidth placeholder="Mobile" value={userDetail.mobile} />
            </Row>
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Name(English)'} required />
            <Row>
              <BasicTextField
                fullWidth
                placeholder="First Name"
                value={userDetail.firstName}
                onChange={(e) => setUserDetail((prev) => ({ ...prev, firstName: e.target.value }))}
              />
              <BasicTextField
                fullWidth
                placeholder="Last Name"
                value={userDetail.lastName}
                onChange={(e) => setUserDetail((prev) => ({ ...prev, lastName: e.target.value }))}
              />
            </Row>
          </Column>
        </Column>

        <Column fullWidth>
          <Column gap="9px">
            <BasicLabel label={'E-mail'} />
            <BasicTextField
              placeholder="E-mail"
              value={userDetail.email}
              onChange={(e) => setUserDetail((prev) => ({ ...prev, email: e.target.value }))}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Birth Date'} required />
            <SingleDatepicker
              fullWidth
              initialDate={stringToDate(userDetail.birthDate)}
              onChange={(date) => {
                if (!date) {
                  return;
                }

                setUserDetail((prev) => ({
                  ...prev,
                  birthDate: dateToString(date)
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Nationality'} required />
            <BasicSelect
              value={userDetail.nationId}
              options={nations.map((nation) => ({
                label: (
                  <Row style={{ alignItems: 'center' }}>
                    <img style={{ width: '20px', height: '20px' }} src={nation.flagUri} />
                    <div>{nation.countryName}</div>
                  </Row>
                ),
                value: nation.nationId
              }))}
              onChange={(e) => {
                setUserDetail((prev) => ({
                  ...prev,
                  nationId: e.target.value as string
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Blood Type'} required />
            <BasicSelect
              value={userDetail.bloodType}
              options={[
                { label: 'A+', value: 'A+' },
                { label: 'A-', value: 'A-' },
                { label: 'B+', value: 'B+' },
                { label: 'B-', value: 'B-' },
                { label: 'AB+', value: 'AB+' },
                { label: 'AB-', value: 'AB-' },
                { label: 'O+', value: 'O+' },
                { label: 'O-', value: 'O-' }
              ]}
              onChange={(e) => {
                setUserDetail((prev) => ({
                  ...prev,
                  bloodType: e.target.value as string
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Iqama No.'} required />
            <BasicTextField
              placeholder="Iqama No"
              value={userDetail.iqamaNo}
              onChange={(e) => setUserDetail((prev) => ({ ...prev, iqamaNo: e.target.value }))}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Iqama Expiry Date'} required />
            <SingleDatepicker
              fullWidth
              initialDate={stringToDate(userDetail.iqamaExpiryDate)}
              onChange={(date) => {
                if (!date) {
                  return;
                }

                setUserDetail((prev) => ({
                  ...prev,
                  iqamaExpiryDate: dateToString(date)
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Authority'} required />
            <BasicTextField value={userDetail.authority} disabled />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'User Status'} required />
            <BasicTextField value={userDetail.userStatus} disabled />
          </Column>
        </Column>

        {isChangePasswordModalOpen && (
          <ChangePasswordForce
            userId={userId}
            open={isChangePasswordModalOpen}
            onClose={() => setIsChangePasswordModalOpen(false)}
          />
        )}
      </Row>

      <Row style={{ justifyContent: 'end' }}>
        {isMyPage && (
          <TextButton
            variant="outlined"
            label={'Withdrawal'}
            onClick={() => {
              showConfirmAlert('Would you like to Withdraw the User information?', () => {
                deleteUser(
                  { data: { userId } },
                  {
                    onSuccess: (data) => {
                      showAlert(data.message, () => {
                        cookieStorage.clearAllCookies();
                        router.push('/login');
                      });
                    },
                    onError: (error) => {
                      showAlert('Failed to Withdraw user');
                    }
                  }
                );
              });
            }}
          />
        )}

        {isMyPage && (
          <TextButton
            style={{ width: '180px' }}
            variant="contained"
            label={'Change Password'}
            onClick={() => {
              setIsChangePasswordModalOpen(true);
            }}
          />
        )}

        <TextButton
          label={'Save'}
          onClick={() => {
            showConfirmAlert('Would you like to Update the User information?', () => {
              const request: ReqUpdateUser = {
                userId: userDetail.userId,
                firstName: userDetail.firstName,
                lastName: userDetail.lastName,
                nationId: userDetail.nationId,
                email: userDetail.email,
                birthDate: userDetail.birthDate,
                bloodType: userDetail.bloodType,
                iqamaNo: userDetail.iqamaNo,
                iqamaExpiryDate: userDetail.iqamaExpiryDate
              };

              updateUser(
                { data: request },
                {
                  onSuccess: (data) => {
                    showAlert(data.message);
                  },
                  onError: (error) => {
                    showAlert('Failed to save user');
                  }
                }
              );
            });
          }}
        />
      </Row>
    </Column>
  );
};
